import React, { useEffect, useState, useMemo } from "react"
import { useShallow } from "zustand/react/shallow"
import { debounce } from "lodash"

// components
import { Tooltip } from "antd"

// utils
import {
  registriesLangTranslationMap,
  registriesWithTranslations,
  stringTranslationFieldSet,
} from "constants/authoring"
import useAuthoringActions from "../../../hooks/actions/useAuthoringActions"
import hasDuplicatesInArrays from "utilities/hasDuplicatesInArrays"
import findDuplicatesInArrays from "utilities/findDuplicatesInArrays"
import scrollToElement from "utilities/scrollToElement"
import { SCROLLABLE_AUTHORING_CONTAINER } from "constants/authoring.js"

// store
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useAuthoringCommentsStore from "../../../hooks/stores/useAuthoringCommentsStore"
import useAuthoringValidationsStore from "../../../hooks/stores/useAuthoringValidationsStore"
import useAuthoringFieldStore from "../../../hooks/stores/useAuthoringFieldStore"
import useAuthoringDataStore from "../../../hooks/stores/useAuthoringDataStore"
import useAuthoringStore from "../../../hooks/stores/useAuthoringStore"
import useAuthoringTranslations from "../../../hooks/stores/useAuthoringTranslations"

// apis
import { useUserPermissions } from "api/hooks"

const useHOC = ({
  props,
  formatOptions,
  // path,
  jpath,
  // jpaths,
  registryKeys,
}) => {
  const { data: permissions } = useUserPermissions()
  const disableValidation = props?.uiSchema?.["ui:options"]?.disableValidation || false

  const { formDataRef } = props.formContext

  const { forkedStudyData, setForkedStudyData, overrideData, setOverrideData, translationsData, setTranslationsData } =
    useAuthoringDataStore(
      useShallow((state) => ({
        forkedStudyData: state.forkedStudyData,
        setForkedStudyData: state.setForkedStudyData,
        overrideData: state.overrideData,
        setOverrideData: state.setOverrideData,
        translationsData: state.translationsData,
        setTranslationsData: state.setTranslationsData,
      }))
    )

  const { registryValidations } = useAuthoringValidationsStore(
    useShallow((state) => ({
      registryValidations: state.registryValidations,
    }))
  )

  const { handleOverride, registryOptions } = useAuthoringActions()

  const { openComment } = useAuthoringCommentsStore(
    useShallow((state) => ({
      openComment: state.openComment,
    }))
  )

  const { formView, formSection, readOnly, isReviewerView } = useAuthoringViewStore(
    useShallow((state) => ({
      isReviewerView: state.isReviewerView,
      formView: state.formView,
      formSection: state.formSection,
      readOnly: state.readOnly,
    }))
  )

  const { lockedFields } = useAuthoringFieldStore(
    useShallow((state) => ({
      lockedFields: state.lockedFields,
    }))
  )

  const { selectedLanguage } = useAuthoringTranslations(
    useShallow((state) => ({
      selectedLanguage: state.selectedLanguage,
    }))
  )

  const [showMenu, setShowMenu] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const [invalidText, setInvalidText] = useState("")
  const [fieldLoading, setFieldLoading] = useState(false)

  let options = formatOptions(props)

  const jpathDotNotation = useMemo(() => {
    return props.id.replace("root.", "")
  }, [props.id])

  const isFieldLocked = useMemo(() => {
    if (lockedFields?.[jpath]) {
      if (overrideData?.[lockedFields?.[jpath]] === undefined) {
        return true
      }
    }
    return false
  }, [jpath, overrideData, lockedFields])

  const isOverriden = overrideData?.[lockedFields?.[jpath]] !== undefined

  const forkedFormViewId = React.useMemo(() => {
    if (formView?.id && formView.id === "eudra") return "eudract"
    return formView?.id
  }, [formView])

  const isForked = forkedStudyData?.[forkedFormViewId]?.hasOwnProperty(jpathDotNotation)

  const value = useMemo(() => {
    let val = props.value

    if (isOverriden) {
      val = overrideData[lockedFields[jpath]]
    } else if (isForked) {
      val = forkedStudyData?.[forkedFormViewId]?.[jpathDotNotation]
    }

    return val
  }, [props.value, forkedStudyData, overrideData, isForked, lockedFields, jpath, forkedFormViewId])

  /**
   * Registry keys that will appear as badges.
   * Only fields that can be forked will have mutiple keys.
   */

  const checkIfForkedJpath = (jpath, openComment) => {
    for (let i = 0; i < registryOptions.length; i++) {
      let option = registryOptions[i]
      let reg = option.value === "eudra" ? "eudract" : option.value

      if (`${jpath}_${reg}` === openComment) {
        return true
      }
    }

    return false
  }

  useEffect(() => {
    if (
      (openComment && (jpath === openComment || `${jpath}_${forkedFormViewId}` === openComment)) ||
      (formView?.id === "global" && checkIfForkedJpath(jpath, openComment))
    ) {
      const scrollableElement = isReviewerView
        ? document.getElementById(SCROLLABLE_AUTHORING_CONTAINER.REVIEW_ID)
        : document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.FORM_CLASSNAME)[0]

      const targetElement = document.getElementById(jpath)

      scrollToElement({ scrollableElement, targetElement, scrollType: "instant", offsetTop: 100 })

      setTimeout(() => {
        scrollToElement({ scrollableElement, targetElement, scrollType: "instant", offsetTop: 100 })
      }, [300])
    }
  }, [openComment])

  const isDisabled = useMemo(() => {
    return isFieldLocked || options.disabled || props.disabled
  }, [isFieldLocked, options?.disabled, props?.disabled])

  const validate = (value) => {
    if (!disableValidation && options.validations) {
      const tempValue = value?.replace ? value.replace(/(\r\n|\n|\r)/gm, "") : value
      for (const validation of options.validations) {
        if (!validation.regex.test(tempValue)) {
          setInvalid(true)
          setInvalidText(validation.message)
          return // Early return if validation fails
        }
      }
    }

    if (invalid) {
      setInvalid(false)
      setInvalidText("")
    }
  }

  const _validate = debounce((data) => validate(data), 300)

  useEffect(() => {
    if (registryValidations && formView.id !== "jrct") {
      // merge Front end validations with Backend if JRCT registry
      setInvalid(false)
      setInvalidText("")
    } else if (formView?.id !== "global") {
      const isRequired = props.required || options.required
      const isFieldEmpty = !value || value === "" || (value && value.length === 0)

      const showRequiredValidation = isRequired && isFieldEmpty

      if (options.required === false) {
        setInvalid(false)
        setInvalidText("")
      } else if (showRequiredValidation) {
        setInvalid(true)
        setInvalidText("This is a required field.")
      } else if (options?.validations?.length) {
        _validate(value)
      } else if (value?.length && (invalid || invalidText)) {
        setInvalid(false)
        setInvalidText("")
      }
    }
  }, [value, options.validations, registryValidations, isOverriden, isForked, formView?.id, formSection?.id, readOnly])

  /**
   * Override functions
   * Unlocked/Revert
   * */
  const unlockField = () => {
    const callback = async () => {
      setFieldLoading(true)
      if (permissions["study.authoringdata.override"]) {
        let updatedOverrideData = {
          ...overrideData,
          [lockedFields[jpath]]: "",
        }

        setOverrideData(updatedOverrideData)
      }

      // if current field value is null and json path is not available - set the json path and field value to
      if (value === undefined) {
        let paths = lockedFields[jpath].split(".")
        let curr = { ...formDataRef }
        for (let i = 0; i < paths.length; i++) {
          let path = paths[i]
          if (i === paths.length - 1) {
            curr[path] = null
          } else {
            if (curr[path]) {
              curr = curr[path]
            } else {
              curr = {}
            }
          }
        }
      }
      await new Promise((r) => setTimeout(r, 500))
      setFieldLoading(false)
    }

    let showOverrideWarning = localStorage.getItem("show-override-warning")

    if (showOverrideWarning === "false") {
      callback()
    } else {
      handleOverride(callback)
    }
  }

  const revertOverride = async (jpath) => {
    setFieldLoading(true)
    let temp = { ...overrideData }
    delete temp[lockedFields[jpath]]

    setOverrideData(temp)

    await new Promise((r) => setTimeout(r, 500))
    setFieldLoading(false)
  }

  /**
   *  On change functions for regular, forked, and override fields
   * */
  const onChange = useMemo(() => {
    return (val, option, isTranslation, fieldType, isForked, forkedRegistryKey) => {
      const onSetTranslationData = () => {
        let tempTranslationsData = { ...translationsData }

        const translationRegistries = findDuplicatesInArrays(registryKeys, registriesWithTranslations)

        translationRegistries.forEach((translationReg) => {
          if (registriesLangTranslationMap[translationReg]) {
            const lang = registriesLangTranslationMap[translationReg]
            if (tempTranslationsData?.[lang]) {
              tempTranslationsData[lang][jpathDotNotation] = val
            } else {
              tempTranslationsData = {
                [lang]: {
                  [jpathDotNotation]: val,
                },
              }
            }
          }
        })

        return tempTranslationsData
      }

      const onUpdateForkedField = () => {
        let forkedKey = forkedRegistryKey === "eudra" ? "eudract" : forkedRegistryKey
        setForkedStudyData({
          ...forkedStudyData,
          [forkedKey]: {
            ...forkedStudyData[forkedKey],
            [jpathDotNotation]: val,
          },
        })
      }

      if (stringTranslationFieldSet.has(fieldType) && hasDuplicatesInArrays(registryKeys, registriesWithTranslations)) {
        let tempTranslationsData = onSetTranslationData()
        setTranslationsData(tempTranslationsData)
        if (isForked) {
          onUpdateForkedField()
        } else if (isOverriden) {
          onOverrideChanged(val)
        } else {
          props.onChange(val)
        }
      } else if (option?.translations) {
        let tempTranslationsData = { ...translationsData }
        Object.keys(option.translations).map((lang) => {
          if (tempTranslationsData?.[lang]) {
            tempTranslationsData[lang][jpathDotNotation] = option.translations[lang]
          } else {
            tempTranslationsData = {
              [lang]: {
                [jpathDotNotation]: option.translations[lang],
              },
            }
          }
        })

        setTranslationsData(tempTranslationsData)

        if (isForked) {
          onUpdateForkedField()
        } else if (isOverriden) {
          onOverrideChanged(val)
        } else {
          props.onChange(val)
        }
      } else if (isTranslation) {
        let tempTranslationsData = onSetTranslationData()
        setTranslationsData(tempTranslationsData)
      } else if (isForked) {
        onUpdateForkedField()
      } else if (isOverriden) {
        onOverrideChanged(val)
      } else {
        props.onChange(val)
      }
    }
  }, [isOverriden, jpathDotNotation, selectedLanguage, translationsData, forkedStudyData, overrideData])

  const displayLockedField = (isLocked, jpath) => {
    let isAllowed = permissions["study.authoringdata.override"]

    if (isAllowed) {
      if (overrideData?.[lockedFields?.[jpath]] !== undefined) {
        return (
          <div
            className="input-wrapper-override-lock"
            onClick={() => revertOverride(jpath)}
            style={{ marginBottom: 2 }}
          >
            <Tooltip title="Revert data">
              <i className="far fa-undo"></i>
            </Tooltip>
          </div>
        )
      } else if (isLocked) {
        return (
          <div
            className="input-wrapper-override-lock"
            onClick={() => (isAllowed ? unlockField() : {})}
          >
            <Tooltip title={isAllowed ? "Override field" : "You don’t have permission to perform this action"}>
              <i className="fas fa-lock-alt"></i>
            </Tooltip>
          </div>
        )
      }
    }

    return null
  }

  const onOverrideChanged = (val) => {
    let temp = { ...overrideData }
    temp[lockedFields[jpath]] = val
    setOverrideData(temp)
  }

  return {
    invalid,
    setInvalid,
    invalidText,
    setInvalidText,
    showMenu,
    setShowMenu,
    options,
    registryKeys,
    // handleOnBlur,
    isFieldLocked,
    unlockField,
    revertOverride,
    onOverrideChanged,
    displayLockedField,
    fieldLoading,
    setFieldLoading,
    isOverriden,
    isDisabled,
    jpathDotNotation,
    value,
    onChange,
    disableValidation,
    forkedFormViewId,
  }
}

export default useHOC
